import { Button, Input, MenuItem, Option, Select } from "@material-tailwind/react"
import AspectRatioDiv from "components/utils/AspectRatioDiv"
import useObject from "hooks/useObject"
import React, { useContext, useEffect, useRef, useState } from "react"
import SimpleBar from "simplebar-react"
import { Category, Event, ReactSetterType } from "utils/types"
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Tiptap from "components/Tiptap"
import { useFloating } from "@floating-ui/react-dom-interactions"
import GalleryPicker from "components/GalleryPicker"
import { useScroll } from "framer-motion"
import { useMediaQuery } from "react-responsive"
import debounce from "lodash.debounce"
import ContentHeightContext from "contexts/ContentHeightContext"
import useResizeObserver from "@react-hook/resize-observer"
import resolveImgUrl from "utils/resolveImgUrl"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import useAxiosPrivate from "hooks/useAxiosPrivate"
import { AlertContext } from "contexts/AlertProvider"
import Spinner from "components/utils/Spinner"
import {Businesses} from 'utils/types'
import ColorPicker from "components/ColorPicker"
import { axiosErrorHandler } from "utils/common"
import { useNavigate } from "react-router-dom"
import DateTimePicker from "react-datetime-picker"

type gallery = [string | null, (string | null)?][]

type EventConstructorType = {
    title : string
    imgUrl : string
    description : string
    rawDescription : string
    category : string| number | null,
    color : string,
    eventDate : string,
}

type EventData = {
    id : number | null,
    title : string,
    imgUrl : string,
    category : string| number | null,
    eventDate : Date,
}

type Prop = {
    setMenuOpened : ReactSetterType<boolean>
    selected : number | null,
}

const EventCreateMenu = ({
        setMenuOpened,
        selected,
} : Prop)=>{
    const [data, setObject, setData] = useObject<EventData>({
        id : null,
        title : '',
        imgUrl : '',
        category : null,
        eventDate : new Date(),
    })

    const axiosPrivate = useAxiosPrivate();
    const queryClient = useQueryClient();
    const navigate = useNavigate()
    const {setAlert} = useContext(AlertContext);

    useEffect(()=>{
        selected && axiosPrivate.get(`/events/${selected}`)
            .then(({data : _data})=>{
                const {description, rawDescription, submittedBy, category, eventDate,...data} : Event = _data;
                
                setDescription(description);
                setRawDescription(rawDescription)
                console.log(_data);
                setData({
                    category : category.id,
                    eventDate : new Date(eventDate),
                    ...data,
                });
            }).catch((err)=>{
                setAlert('error', 'Cannot fetch events, contact admin for further information')
            })
    }, [selected])

    const {data: categories, status : categoriesStatus, error : categoriesError, refetch : refetchCategories} = useQuery(['event-categories'], async()=>{
        const {data} = await axiosPrivate.get('event-category?limit=1000');
        return data[0] as Category[];
    })



    async function postHandler(data : EventConstructorType & {id : number | null}){
        if(!data.id)
            return axiosPrivate.post('events', data);
        else
            return axiosPrivate.put(`events/${data.id}`, data);
    }

    const {mutate : postEvent, isLoading} = useMutation(postHandler, {
      onSuccess(){
        queryClient.invalidateQueries(['events']); 

        if(!isNaN(data.category as any)){
          queryClient.invalidateQueries(['event-categories']); 
        }

        if(!data.id)
            setAlert('success', 'events Posted !')
        else
            setAlert('success', 'events Updated !')

        setMenuOpened(false);
      },
      onError: axiosErrorHandler(navigate, setAlert)
    })

    function submitHandler(){
        async function submit(){
            console.log(data);
            if(!data.title)
                throw new Error('Title cant be empty')
            if(!data.imgUrl)
                throw new Error('Main image must be choosen')
            if(!data.eventDate)
                throw new Error('Date cant be empty')
            if(!rawDescription){
                throw new Error('Description cant be empty')
            }
            if(!data.category || data.category === ""){
                throw new Error('Category cant be empty')
            }

            //@ts-ignore
            postEvent({
                ...data, description, rawDescription, color : color || "2196f3", eventDate : data.eventDate.toISOString()
            })
        }
            
        submit().catch((err:Error)=>setAlert('error', err.message))
    } 

    const root = useRef<HTMLDivElement>(null);
    const isMobile = useMediaQuery({ query: '(max-width: 960px)' });

    const [description, setDescription] = useState('')
    const [rawDescription, setRawDescription] = useState('')

    const [overlayHeight, setOverlayHeight] = useState<string | undefined>(undefined);

    const {height : contentHeight} = useContext(ContentHeightContext);
    const [color, setColor] = useState<string | undefined>("2196f3");

    useResizeObserver(root, (entry) => isMobile ? setOverlayHeight(`calc(${entry.contentRect.height}px + 5rem)`) : setOverlayHeight(undefined))

    if(selected && !(data.id))
        return(
            <div className="absolute left-0 top-0 w-full" style={{height : contentHeight}}>
                <div className="sticky z-10 top-0 left-0 w-full h-screen">
                    <div className="relative w-full h-full">
                        <div className="absolute top-0 left-0 w-full h-full bg-black opacity-30 z-[11]" style={{height : overlayHeight}} onClick={()=>setMenuOpened(false)}></div>
                        <div className="absolute top-0 left-0 w-full h-full z-[12] grid place-content-center">
                            <Spinner></Spinner>
                        </div>
                    </div>
                </div>
            </div>
        )
    else
    return (
        <div className="absolute left-0 top-0 w-full" style={{height : contentHeight}}>
            <div className="sticky z-10 top-0 left-0 w-full h-screen">
                <div className="relative w-full h-full">
                    <SimpleBar className="z-[11] h-screen" id="create-menu-simplebar">
                        <div className="w-full h-full flex my-10 lg:my-0 lg:items-center justify-center" ref={root}>
                            <div className="absolute top-0 left-0 w-full lg:h-full bg-black opacity-30 z-[11]" style={{height : overlayHeight}} onClick={()=>setMenuOpened(false)}></div>
                            <div className="w-[85%] lg:h-[85%] relative z-[11]">
                                <div className="flex gap-x-2 relative w-full h-full gap-y-2">
                                    <div className=" w-full flex flex-col lg:flex-row gap-2">
                                        <div className="w-full lg:w-72 xl:w-80 2xl:w-96 flex flex-col items-center gap-y-2 relative">
                                            <div className="w-full p-3 bg-white rounded-lg">
                                                <AspectRatioDiv className="pt-[56.25%]">
                                                    <img 
                                                        src={data.imgUrl ? resolveImgUrl(data.imgUrl) : "https://via.placeholder.com/640x360.png?text=(16:9+aspect-ratio)"}
                                                        className="w-full h-full bg-cover" 
                                                        alt="" 
                                                    />
                                                    <GalleryPicker postImageUrl='/events/image' onSelect={(e)=>setObject({imgUrl : e})}>
                                                        {(reference, referenceProp)=>
                                                            <div tabIndex={1} ref={reference} {...referenceProp} className="absolute right-0 bottom-0 w-8 h-8 grid place-content-center bg-slate-800 group cursor-pointer">
                                                                <i className="fas fa-images text-white group-hover:text-sky-200"></i>
                                                            </div>
                                                        }
                                                    </GalleryPicker>
                                                </AspectRatioDiv>
                                            </div>
                                            <div className="w-full p-3 bg-white rounded-lg flex flex-col gap-y-3">
                                                <Input label="Title" 
                                                tabIndex={2}
                                                onChange={(e)=>setObject({title:e.target.value})} 
                                                value={data.title}/>
                                                {/* TODO: Build category picker */}
                                                {typeof data.category === 'string' 
                                                ?<div className="flex gap-3">
                                                    <Input label="Category" 
                                                        tabIndex={3}
                                                        onChange={(e)=>setObject({category:e.target.value})} 
                                                        value={data.category || ''}/>
                                                    <ColorPicker onColorChange={(c)=>setColor(c)} initialColor={color}>
                                                        {(reference, referenceProp)=>
                                                            <div
                                                            ref={reference} {...referenceProp}
                                                            className="w-14 rounded-md cursor-pointer" style={{backgroundColor:`#${color}`}}/>
                                                        }
                                                    </ColorPicker>
                                                    <div className="border w-14 rounded-md border-gray-400 text-gray-400 grid place-content-center cursor-pointer hover:bg-blue-500 hover:text-white" onClick={()=>setObject({category:null})}>
                                                        <i className="fas fa-ellipsis text-xl"></i>
                                                    </div>
                                                </div>
                                                : categoriesStatus === "success" 
                                                    ?<div className="flex gap-3">
                                                        <Select onChange={(e)=>setObject({category:parseInt(e as string)})} value={data.category? data.category + "" : undefined} label="Select Category">
                                                            {
                                                                categories?.map((category)=><Option key={category.id} value={category.id+""}>
                                                                    {category.title}
                                                                </Option>)
                                                            }
                                                        </Select>
                                                        <div className="border w-14 rounded-md border-gray-400 text-gray-400 grid place-content-center cursor-pointer hover:bg-blue-500 hover:text-white" onClick={()=>setObject({category:""})}>
                                                            <i className="fas fa-plus text-xl"></i>
                                                        </div>
                                                    </div> : 
                                                    <Spinner></Spinner>
                                                }
                                                <DateTimePicker
                                                  disableClock={true}
                                                  onChange={(value)=>setObject({eventDate:(value as Date)})}
                                                  value={data.eventDate}
                                                />
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-lg grow lg:h-full">
                                            <Tiptap 
                                                tabIndex={5}
                                                rawText={rawDescription} 
                                                setRawText={setRawDescription} 
                                                text={description} 
                                                setText={setDescription} 
                                                placeholder="<h2>Hi there,</h2><p>Write description here</p>"
                                                className="h-full"
                                            />
                                        </div>
                                    </div>
                                    <div className="absolute bottom-4 right-4">
                                        <Button size="lg" disabled={isLoading} onClick={submitHandler}>
                                            Submit
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </SimpleBar>
                </div>
            </div>
        </div>
    )
}

export default EventCreateMenu;